"use client";

import { trackProductTileBrandLinkHover } from "@faire/web-api/events/productTile/hover/brandLink";
import { IFaireIcon } from "@faire/web-api/indigofair/data/IFaireIcon";
import React, { ReactNode, useCallback, useMemo } from "react";

import { handleGenerateBrandUrl } from "@faire/retailer/components/SearchProducts/utils";
// eslint-disable-next-line @faire/enforce-internal-folder-encapsulation
import { useOnBrandClickSearchPage } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/BrandInfo/__internal__/SearchProductTileBrandInfoClickContextProvider";
import * as RetailerPaths from "@faire/retailer/paths";
import { useSearchPageProductSearchString } from "@faire/retailer/views/search-v2/SearchPageQueryParams/useSearchPageProductSearchString";

import { MarketplaceProductBrandLink } from "../../MarketplaceProductBrandLink";

interface ISearchMarketplaceProductBrandLinkProps {
  brandToken: string | undefined;
  brandIcon?: IFaireIcon | undefined;
  onBrandLinkClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  generateBrandUrl?: (brandToken: string) => string | undefined;
  children: ReactNode;
}

export const MarketplaceProductBrandLinkBase: React.FC<
  ISearchMarketplaceProductBrandLinkProps
> = ({ brandToken, children, onBrandLinkClick, generateBrandUrl }) => {
  const trackingParamsBrandClickHandler = useOnBrandClickSearchPage({});

  const onBrandClick = useCallback(
    (ev: React.MouseEvent<HTMLAnchorElement>) => {
      if (onBrandLinkClick) {
        onBrandLinkClick(ev);
      } else {
        trackingParamsBrandClickHandler.onBrandClick(ev);
      }
    },
    [trackingParamsBrandClickHandler, onBrandLinkClick]
  );
  const onBrandLinkHover = useCallback(() => {
    trackProductTileBrandLinkHover();
  }, []);
  const onBrandLinkMouseEnter = useCallback(() => {
    trackProductTileBrandLinkHover();
  }, []);
  const queryString = useSearchPageProductSearchString();
  const generateBrandUrlDefault = handleGenerateBrandUrl(queryString, {
    brandToken: brandToken,
    productToken: undefined,
    impressionId: undefined,
  });
  const generateBrandUrlFn = generateBrandUrl ?? generateBrandUrlDefault;

  const brandUrl = useMemo(() => {
    if (!brandToken) {
      return undefined;
    }
    if (!generateBrandUrlFn) {
      return RetailerPaths.brandPage(brandToken);
    }
    return generateBrandUrlFn(brandToken);
  }, [brandToken, generateBrandUrlFn]);

  return (
    <MarketplaceProductBrandLink
      token={brandToken}
      onClick={(ev) => {
        onBrandClick?.(ev);
      }}
      onMouseEnter={onBrandLinkHover}
      onTouchStart={onBrandLinkMouseEnter}
      data-test-id="marketplace-product-brand-link"
      to={brandUrl}
    >
      {children}
    </MarketplaceProductBrandLink>
  );
};
